import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';
import { friendsAPI, usersAPI } from '../services/api';
import { User, Friendship } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`friends-tabpanel-${index}`}
      aria-labelledby={`friends-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface FriendsProps {
  currentUserId: string;
}

const Friends: React.FC<FriendsProps> = ({ currentUserId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [sentRequests, setSentRequests] = useState<Friendship[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<Friendship[]>([]);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addFriendDialog, setAddFriendDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [friendMessage, setFriendMessage] = useState('');

  useEffect(() => {
    loadFriendsData();
  }, [currentUserId]);

  const loadFriendsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [friendsResponse, sentResponse, receivedResponse] = await Promise.all([
        friendsAPI.getFriends(),
        friendsAPI.getSentRequests(),
        friendsAPI.getReceivedRequests()
      ]);

      setFriends(friendsResponse.data);
      setSentRequests(sentResponse.data);
      setReceivedRequests(receivedResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load friends data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await usersAPI.searchUsers(query, 10);
      setSearchResults(response.data);
    } catch (err: any) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSendFriendRequest = async (user: User) => {
    try {
      await friendsAPI.sendFriendRequest({
        addressee_id: user.id,
        message: friendMessage
      });

      setAddFriendDialog(false);
      setSelectedUser(null);
      setFriendMessage('');
      await loadFriendsData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send friend request');
    }
  };

  const handleAcceptRequest = async (request: Friendship) => {
    try {
      await friendsAPI.acceptFriendRequest(request.id, {});
      await loadFriendsData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept friend request');
    }
  };

  const handleDeclineRequest = async (request: Friendship) => {
    try {
      await friendsAPI.declineFriendRequest(request.id, {});
      await loadFriendsData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to decline friend request');
    }
  };

  const handleRemoveFriend = async (friend: Friendship) => {
    try {
      const friendId = friend.requester_id === currentUserId ? friend.addressee_id : friend.requester_id;
      await friendsAPI.removeFriend(friendId, {});
      await loadFriendsData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove friend');
    }
  };

  const handleBlockUser = async (friend: Friendship) => {
    try {
      const friendId = friend.requester_id === currentUserId ? friend.addressee_id : friend.requester_id;
      await friendsAPI.blockUser(friendId, {});
      await loadFriendsData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to block user');
    }
  };

  const openAddFriendDialog = (user: User) => {
    setSelectedUser(user);
    setAddFriendDialog(true);
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { color: any; label: string }> = {
      pending: { color: 'warning', label: 'Pending' },
      accepted: { color: 'success', label: 'Friends' },
      declined: { color: 'error', label: 'Declined' },
      blocked: { color: 'error', label: 'Blocked' }
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Friends</Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddFriendDialog(true)}
          >
            Add Friend
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label={`Friends (${friends.length})`} />
          <Tab label={`Requests (${receivedRequests.length})`} />
          <Tab label={`Sent (${sentRequests.length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <List>
            {friends.length === 0 ? (
              <Typography variant="body1" color="textSecondary" textAlign="center" py={4}>
                No friends yet. Start by sending some friend requests!
              </Typography>
            ) : (
              friends.map((friendship) => {
                const friend = friendship.requester_id === currentUserId 
                  ? friendship.addressee 
                  : friendship.requester;
                
                if (!friend) return null;

                return (
                  <ListItem key={friendship.id}>
                    <ListItemAvatar>
                      <Avatar src={friend.avatar_url}>
                        {friend.display_name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={friend.display_name}
                      secondary={`@${friend.username}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFriend(friendship)}
                        color="error"
                        sx={{ mr: 1 }}
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleBlockUser(friendship)}
                        color="error"
                      >
                        <BlockIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })
            )}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <List>
            {receivedRequests.length === 0 ? (
              <Typography variant="body1" color="textSecondary" textAlign="center" py={4}>
                No friend requests
              </Typography>
            ) : (
              receivedRequests.map((request) => (
                <ListItem key={request.id}>
                  <ListItemAvatar>
                    <Avatar src={request.requester?.avatar_url}>
                      {request.requester?.display_name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={request.requester?.display_name}
                    secondary={
                      <Box>
                        <Typography variant="body2">@{request.requester?.username}</Typography>
                        {request.message && (
                          <Typography variant="body2" color="textSecondary">
                            "{request.message}"
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleAcceptRequest(request)}
                      color="success"
                      sx={{ mr: 1 }}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeclineRequest(request)}
                      color="error"
                    >
                      <CloseIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            )}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <List>
            {sentRequests.length === 0 ? (
              <Typography variant="body1" color="textSecondary" textAlign="center" py={4}>
                No sent requests
              </Typography>
            ) : (
              sentRequests.map((request) => (
                <ListItem key={request.id}>
                  <ListItemAvatar>
                    <Avatar src={request.addressee?.avatar_url}>
                      {request.addressee?.display_name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={request.addressee?.display_name}
                    secondary={`@${request.addressee?.username}`}
                  />
                  <ListItemSecondaryAction>
                    {getStatusChip(request.status)}
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            )}
          </List>
        </TabPanel>
      </Paper>

      <Dialog open={addFriendDialog} onClose={() => setAddFriendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Friend</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handleSearch(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchLoading ? <CircularProgress size={20} /> : null
            }}
            sx={{ mb: 2 }}
          />

          {selectedUser ? (
            <Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar src={selectedUser.avatar_url} sx={{ mr: 2 }}>
                  {selectedUser.display_name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUser.display_name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    @{selectedUser.username}
                  </Typography>
                </Box>
              </Box>
              
              <TextField
                fullWidth
                label="Message (optional)"
                multiline
                rows={3}
                value={friendMessage}
                onChange={(e) => setFriendMessage(e.target.value)}
                placeholder="Hi, I'd like to be friends!"
              />
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openAddFriendDialog(user)}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar_url}>
                      {user.display_name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.display_name}
                    secondary={`@${user.username}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddFriendDialog(false);
            setSelectedUser(null);
            setFriendMessage('');
            setSearchQuery('');
            setSearchResults([]);
          }}>
            Cancel
          </Button>
          {selectedUser && (
            <Button
              variant="contained"
              onClick={() => handleSendFriendRequest(selectedUser)}
            >
              Send Request
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Friends;